






























































































import { Component, Vue, Prop } from "vue-property-decorator";
import AgroGenericModal from "@/components/agroGenericModal/AgroGenericModal.vue";
import AgroIcons from "@/components/agroIcons/AgroIcons.vue";
import { CropsDTO } from "@/api/crops/types";
import { CropType } from "@/constants/crop-type";
import * as cropsService from "@/services/crops";
import * as notificationService from "@/services/notification";
import i18n from "@/i18n";

interface EditableRow {
  cropUuid: string;
  cropLabel: number;
  harvestSeason: string;
  // valores editáveis (ligados ao formulário)
  cropType: string;
  yield: number | string;
  seedingDate: string;
  harvestDate: string;
  // valores originais para diff
  original: {
    cropType: string;
    yield: number;
    seedingDate: string;
    harvestDate: string;
  };
}

@Component({
  name: "HarvestDataEditor",
  components: {
    AgroGenericModal,
    AgroIcons,
  },
})
export default class HarvestDataEditor extends Vue {
  @Prop({ type: String, required: true })
  private analysisUuid!: string;

  @Prop({ type: Array, default: () => [] })
  private crops!: CropsDTO[];

  @Prop({ type: String, default: "SUMMER" })
  private harvestSeason!: string;

  // Quando preenchida, restringe as opções de cultura a esta lista.
  // Vazia/null => lista completa do enum (comportamento padrão).
  @Prop({ type: Array, default: null })
  private allowedCropTypes!: string[] | null;

  private modalVisible = false;
  private saving = false;
  private rows: EditableRow[] = [];

  private get isAdminUser(): boolean {
    return this.$store.getters["user/isAdminUser"];
  }

  private get cropTypeOptions(): string[] {
    // Lista restrita (ex.: SENTINELA não unificado => só a cultura da análise).
    if (this.allowedCropTypes && this.allowedCropTypes.length) {
      return this.allowedCropTypes;
    }
    // Todas as culturas do enum (exceto o fallback literal "null" e WINTER_CORN,
    // que não deve ser selecionável manualmente — usa-se apenas CORN).
    return Object.keys(CropType).filter(
      key => key !== "null" && key !== "WINTER_CORN"
    );
  }

  private toInputDate(value: Date | string | null | undefined): string {
    if (!value) {
      return "";
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return "";
    }
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  private buildRows(): EditableRow[] {
    return (this.crops || []).flat().map((crop: CropsDTO) => {
      const seedingDate = this.toInputDate(crop.seedingDate);
      // Para colheita exibimos a data efetiva (real ou estimada) como baseline.
      const harvestDate = this.toInputDate(crop.harvestDate || crop.estimatedHarvestDate);
      const cropType = crop.cropType;
      const yieldValue = crop.yield;

      return {
        cropUuid: crop.cropUuid,
        cropLabel: crop.cropLabel,
        harvestSeason: crop.harvestSeason || this.harvestSeason,
        cropType,
        yield: yieldValue,
        seedingDate,
        harvestDate,
        original: {
          cropType,
          yield: yieldValue,
          seedingDate,
          harvestDate,
        },
      };
    });
  }

  private openModal() {
    if (this.crops.length === 0) {
      return;
    }
    this.rows = this.buildRows();
    this.modalVisible = true;
  }

  private closeModal() {
    this.modalVisible = false;
  }

  private buildEdits() {
    const edits = [];
    for (const row of this.rows) {
      const edit: any = {
        crop_uuid: row.cropUuid,
        harvest_season: row.harvestSeason,
        crop_type: row.original.cropType,
      };
      let changed = false;

      if (row.cropType !== row.original.cropType) {
        edit.new_crop_type = row.cropType;
        edit.crop_type_confirmed = true;
        changed = true;
      }

      if (row.yield !== "" && row.yield !== null) {
        const yieldValue = Number(row.yield);
        if (!isNaN(yieldValue) && yieldValue !== Number(row.original.yield)) {
          edit.yield = yieldValue;
          changed = true;
        }
      }

      if (row.seedingDate && row.seedingDate !== row.original.seedingDate) {
        edit.seeding_date = row.seedingDate;
        changed = true;
      }

      if (row.harvestDate && row.harvestDate !== row.original.harvestDate) {
        edit.harvest_date = row.harvestDate;
        changed = true;
      }

      if (changed) {
        edits.push(edit);
      }
    }
    return edits;
  }

  private async save() {
    const edits = this.buildEdits();
    if (edits.length === 0) {
      notificationService.addWarnNotification(
        i18n.t("harvest-data-editor.no-changes") as string
      );
      return;
    }

    this.saving = true;
    try {
      const ok = await cropsService.overrideHarvestData(this.analysisUuid, edits);
      if (ok) {
        this.modalVisible = false;
        this.$emit("saved");
      }
    } finally {
      this.saving = false;
    }
  }
}
